import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { PaymentMethod } from '@/lib/shared-types';
import useCurrency from '@/Hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { apiMessage, DELIVERY_ROUTES, PROOF_TYPES, type Delivery } from '../deliveryApi';

const TENDER_METHODS: PaymentMethod[] = Object.values(PaymentMethod);

/**
 * Proof of delivery and cash collection, recorded together — the one call
 * that actually happens at the door, matching `MarkDeliveredDto`.
 *
 * **This is the redteam finding, closed on the client side.** A red-team pass
 * found `markDelivered` would accept `tenderMethod: CASH` with
 * `collectedAmount` omitted on this branch (`dev`) — the delivery still went
 * to DELIVERED, the driver's shift never learned about the cash, and the
 * shift could close with zero variance while money was actually short. The
 * fix (`redteam/rt-params-wallet`) makes the backend refuse that request; this
 * dialog does not wait for that branch to merge — it makes the same request
 * unsendable from here. Choosing CASH swaps the amount field from optional to
 * required and disabled-until-filled, not a hint that a required field is
 * missing after the fact.
 */
export function DeliverDialog({
  delivery,
  onClose,
  onDone,
}: {
  delivery: Delivery;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { decimals, code } = useCurrency(delivery.branchId);
  const step = decimals === 0 ? '1' : `0.${'0'.repeat(decimals - 1)}1`;

  const [proofType, setProofType] = useState<string>('NONE');
  const [proofRef, setProofRef] = useState('');
  const [proofNote, setProofNote] = useState('');
  const [tenderMethod, setTenderMethod] = useState<string>('');
  const [collectedAmount, setCollectedAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isCash = tenderMethod === PaymentMethod.CASH;
  const cashMissing = isCash && !collectedAmount.trim();

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(DELIVERY_ROUTES.deliver(delivery.id), {
        proofType,
        proofRef: proofRef.trim() || undefined,
        proofNote: proofNote.trim() || undefined,
        tenderMethod: tenderMethod || undefined,
        // Never sent when not CASH — the backend's own optionality, matched
        // rather than sending an empty string it would have to reject.
        collectedAmount: isCash ? collectedAmount.trim() : undefined,
      });
      toast.success(t('delivery.board.deliverDone'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.board.actionFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('delivery.board.markDelivered')}
      description={t('delivery.board.markDeliveredHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting || cashMissing}>
            {t('delivery.board.confirmDelivered')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('delivery.board.proofType')}>
          <Select value={proofType} onChange={(e) => setProofType(e.target.value)}>
            {PROOF_TYPES.map((option) => (
              <option key={option} value={option}>
                {t(`delivery.proofType.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        {proofType !== 'NONE' && (
          <Field
            label={t('delivery.board.proofRef')}
            hint={t('delivery.board.proofRefHint')}
          >
            <Input value={proofRef} onChange={(e) => setProofRef(e.target.value)} />
          </Field>
        )}

        <Field label={t('delivery.board.proofNote')}>
          <Textarea
            rows={2}
            value={proofNote}
            onChange={(e) => setProofNote(e.target.value)}
            maxLength={1000}
          />
        </Field>

        <Field label={t('delivery.board.tenderMethod')} hint={t('delivery.board.tenderMethodHint')}>
          <Select value={tenderMethod} onChange={(e) => setTenderMethod(e.target.value)}>
            <option value="">{t('delivery.board.tenderMethodNone')}</option>
            {TENDER_METHODS.map((method) => (
              <option key={method} value={method}>
                {t(`billing.method.${method}`)}
              </option>
            ))}
          </Select>
        </Field>

        {/*
          The field this whole dialog exists to protect. It only appears once
          CASH is chosen, and it is REQUIRED the moment it appears — there is
          no path through this form that reaches "CASH" and an empty amount at
          the same time, which is the exact hole the redteam finding named.
        */}
        {isCash && (
          <Field
            label={t('delivery.board.collectedAmount', { currency: code })}
            required
            error={cashMissing ? t('delivery.board.collectedAmountRequired') : undefined}
          >
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step={step}
              value={collectedAmount}
              onChange={(e) => setCollectedAmount(e.target.value)}
              autoFocus
            />
          </Field>
        )}

        {isCash && (
          <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
            <Icon as={TriangleAlert} size="sm" className="mt-0.5 shrink-0" />
            {t('delivery.board.cashShiftHint')}
          </p>
        )}
      </div>
    </Dialog>
  );
}

export default DeliverDialog;
